import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Modal,
  MenuItem,
  Select,
  Alert,
  CircularProgress
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import cross from "../../assets/img/cross.png";
import { updateSubRegion } from "../../api/Api";

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40vw",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: "none",
};

const UpdateSubregion = ({
  open,
  handleClose,
  onSuccess,
  initialRegion,
  initialSubRegions = [],
  regionId,
}) => {
  const theme = useTheme();
  
  const [formState, setFormState] = useState({
    region: "",
    subRegions: [],
    selectedSubRegion: null,
    error: "",
    isSubmitting: false
  });

  const normalizedSubRegions = useMemo(() => 
    initialSubRegions.map(subRegion => ({
      _id: subRegion._id,
      region_name: subRegion.region_name || subRegion.name || ""
    })), [initialSubRegions]);

  useEffect(() => {
    if (open) {
      setFormState({
        region: initialRegion || "",
        subRegions: normalizedSubRegions,
        selectedSubRegion: null,
        error: "",
        isSubmitting: false
      });
    }
  }, [open, initialRegion, normalizedSubRegions]);

  const handleSubRegionChange = (newValue) => {
    if (!formState.selectedSubRegion) return;

    setFormState(prev => ({
      ...prev,
      subRegions: prev.subRegions.map(subRegion =>
        subRegion._id === prev.selectedSubRegion._id
          ? { ...subRegion, region_name: newValue }
          : subRegion
      ),
      selectedSubRegion: {
        ...prev.selectedSubRegion,
        region_name: newValue
      }
    }));
  };

  const handleSubRegionSelect = (subRegionId) => {
    const selected = formState.subRegions.find(
      subRegion => subRegion._id === subRegionId
    );
    setFormState(prev => ({
      ...prev,
      selectedSubRegion: selected || null,
      error: ""
    }));
  };

  const validateForm = () => {
    if (!regionId) {
      setFormState(prev => ({
        ...prev,
        error: "Region ID is required"
      }));
      return false;
    }
    
    if (!formState.selectedSubRegion?._id) {
      setFormState(prev => ({
        ...prev,
        error: "Please select a sub-region to update"
      }));
      return false;
    }

    if (!formState.selectedSubRegion.region_name.trim()) {
      setFormState(prev => ({
        ...prev,
        error: "Sub-region name is required"
      }));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setFormState(prev => ({ ...prev, isSubmitting: true, error: "" }));

    try {
      // Prepare data according to backend API requirements
      const updateData = {
        regionId,
        subRegionId: formState.selectedSubRegion._id,
        newSubRegionName: formState.selectedSubRegion.region_name
      };

      // Call the update API
      const response = await updateSubRegion(updateData);

      if (onSuccess) {
        await onSuccess(response.data.region);
      }
      
      handleClose();
    } catch (error) {
      console.error("Error updating sub-region:", error);
      setFormState(prev => ({
        ...prev,
        error: error.response?.data?.message || "Failed to update sub-region. Please try again.",
        isSubmitting: false
      }));
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="update-region-modal-title"
    >
      <Box sx={MODAL_STYLE} component="form" onSubmit={handleSubmit}>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.main,
            padding: "20px",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography id="update-region-modal-title" variant="h6">
            Update Sub-Region
          </Typography>
          <Box
            component="img"
            src={cross}
            alt="Close"
            sx={{
              width: "30px",
              height: "30px",
              cursor: "pointer",
            }}
            onClick={!formState.isSubmitting ? handleClose : undefined}
          />
        </Box>

        <Box sx={{ padding: "20px" }}>
          {formState.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formState.error}
            </Alert>
          )}

          <TextField
            label="Region Name"
            fullWidth
            margin="normal"
            value={formState.region}
            disabled
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Sub-Regions:
          </Typography>
          <Select
            fullWidth
            value={formState.selectedSubRegion?._id || ""}
            onChange={(e) => handleSubRegionSelect(e.target.value)}
            displayEmpty
            disabled={formState.isSubmitting}
          >
            <MenuItem value="" disabled>
              Select a Sub-Region to Edit
            </MenuItem>
            {formState.subRegions.map((subRegion) => (
              <MenuItem key={subRegion._id} value={subRegion._id}>
                {subRegion.region_name}
              </MenuItem>
            ))}
          </Select>

          {formState.selectedSubRegion && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body1" gutterBottom>
                New Sub-Region Name:
              </Typography>
              <TextField
                fullWidth
                value={formState.selectedSubRegion.region_name}
                onChange={(e) => handleSubRegionChange(e.target.value)}
                disabled={formState.isSubmitting}
                error={Boolean(formState.error && !formState.selectedSubRegion.region_name.trim())}
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            padding: "20px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2
          }}
        >
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={formState.isSubmitting}
            startIcon={formState.isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {formState.isSubmitting ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            disabled={formState.isSubmitting}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateSubregion;